import { v4 as UUIDv4 } from 'uuid';
import { getImageParams, request, requestModelDetail } from '@shakkerai/services';
import { getPartialState, ImageKey, updatePartialState } from './state';

import { setTempState } from '@starflow/app/store';
import { getUnitData } from '@starflow/app/helpers/flow';
import { detailInfoToGenerateInfo } from '@shakkerai/pro-utils';
import { notifyError } from '@shakkerai/pro-components';
import { modelTypeLabelsMap } from '@shakkerai/pro-models';

export const loadTagsByPrompt = async (prompt) => {
  try {
    const result = await request('/api/www/xxxx', { prompt });
    return result.data || [];
  } catch {
    return [];
  }
};

const cachedImageData = {};
const SCENE_CONTROL_TAG_CODE = '236ffa801b4742c493e31133f2dcab49';

export const showImageGenerationInfo = async (uuid, windowTop) => {
  // uuid = '8f8bed13a3944a89a10ceeedb544faba';

  try {
    let imageData = cachedImageData[uuid];

    if (!imageData) {
      imageData = await getImageParams({ styleImageUuid: uuid }).then((res) => res.data);
    }

    if (!imageData || !imageData.extraInfo) throw { message: null };

    cachedImageData[uuid] = imageData;

    const params = detailInfoToGenerateInfo(imageData);
    params.isGalleryImage = true;

    const imagesWindowElement = document.querySelector('[data-role="images-window"]');

    let generationWindowLeft = 100;
    let generationWindowTop = windowTop;

    if (imagesWindowElement) {
      generationWindowLeft = imagesWindowElement.getBoundingClientRect().left + imagesWindowElement.offsetWidth + 10;
    }

    if (generationWindowLeft + 300 > window.innerWidth) {
      generationWindowLeft = window.innerWidth - 300;
    }

    setTempState({
      generationWindowId: uuid,
      generationWindowLeft,
      generationWindowTop,
      thumbImageInfo: imageData,
      thumbGenerationInfo: params,
    });
  } catch (error) {
    console.log(`[DEBUG] error`, error);
    notifyError('No image generation information!');
  }
};

export const loadImagesByPrompt = async (prompt, key, isRecommend = false) => {
  let storePath = `${key}.data`;
  let filterPath = `${key}.filters`;
  try {
    const { checkpoint, loraModels } = getUnitData();
    const tagsId = checkpoint?.tagV2Ids || [];
    const loraModelTagsId = loraModels?.map((model) => model.tagV2Ids || []) || [];
    const allTagsId = tagsId.concat(...loraModelTagsId).filter((item, index, array) => array.indexOf(item) === index);

    updatePartialState(storePath, { loading: true });
    updatePartialState(filterPath, { prompt, page: 1 });

    let sort = undefined;
    if (key === ImageKey.hotImages) {
      sort = '2';
    }
    if (key === ImageKey.recommendImages) {
      sort = undefined;
    }
    if (key === ImageKey.allImages) {
      if (prompt) {
        sort = undefined;
      } else {
        sort = '2';
      }
    }

    const result = await request(`${isRecommend ? '/api/www/image/tool/cases' : '/api/www/image/tool/cases/search'}`, {
      keyword: prompt,
      tagsId: allTagsId,
      page: 1,
      pageSize: 200,
      sort,
      requestId: UUIDv4(),
    });
    // const sleep = () => {
    //   return new Promise((r) => {
    //     setTimeout(r, 2000);
    //   });
    // };
    // await sleep();
    if (result.data.data) {
      const picArr = result.data.data.filter(
        (item, index, array) => array.findIndex((i) => i.uuid === item.uuid) === index,
      );
      updatePartialState(storePath, {
        loading: false,
        items: picArr,
        hasMore: result.data.data.length && result.data.hasMore,
      });
    } else {
      updatePartialState(storePath, {
        loading: false,
        items: [],
        hasMore: false,
      });
    }
  } catch (error) {
    console.log(`[DEBUG] error`, error);
    updatePartialState(storePath, { loading: false, hasMore: false });
  }
};

export const loadModelImages = async (versionUuid, modelUuid, type = 'case') => {
  try {
    updatePartialState(`modelImages.${type}.data`, { loading: true, items: [] });
    updatePartialState(`modelImages.${type}.filters`, { page: 1 });

    let modelData = getPartialState('modelImages.modelData');

    if (!modelData || modelData.uuid !== modelUuid) {
      updatePartialState(`modelImages`, { modelData: null });
      modelData = await requestModelDetail(modelUuid).then((res) => res?.data);

      if (!modelData || !modelData.versions?.length) throw { message: null };

      modelData.isSceneControlModel = modelData.tagsV2?.modelContent?.some(
        (tag) => tag.code === SCENE_CONTROL_TAG_CODE,
      );

      updatePartialState(`modelImages.modelData`, modelData);
    }

    if (!versionUuid && type === 'case') {
      versionUuid = modelData.versions[0].uuid;
    }

    console.log(`[DEBUG] modelData`, modelData);

    const result = await request('/api/www/image/tool/modelCases', {
      modelUuid: modelUuid,
      versionUuid: versionUuid,
      modelType: modelTypeLabelsMap[modelData.modelType] || modelData.modelType,
      imageType: type === 'case' ? 'sample' : 'return',
      sceneControl: modelData.isSceneControlModel ? 1 : 0,
      page: 1,
      pageSize: 30,
    });

    if (!result?.data) throw { message: null };

    updatePartialState(`modelImages.${type}.data`, {
      loading: false,
      items: result.data.data || [],
      hasMore: result.data.data?.length && result.data.hasMore,
    });
  } catch (error) {
    console.log(`[DEBUG] error`, error);
    updatePartialState(`modelImages.${type}.data`, { loading: false, hasMore: false });
  }
};

export const loadMoreImages = async () => {
  try {
    const { prompt, page, pageSize } = getPartialState('images.filters');
    const { loading, hasMore } = getPartialState('images.data');

    if (loading || !hasMore) return;

    const { checkpoint, loraModels } = getUnitData();
    const tagsId = checkpoint?.tagV2Ids || [];
    const loraModelTagsId = loraModels?.map((model) => model.tagV2Ids || []) || [];
    const allTagsId = tagsId.concat(...loraModelTagsId).filter((item, index, array) => array.indexOf(item) === index);

    updatePartialState('images.data', { loading: true });
    updatePartialState('images.filters', { page: page + 1 });

    const result = await request('/api/www/image/tool/cases', {
      keyword: prompt,
      tagsId: allTagsId,
      page: page + 1,
      pageSize,
      requestId: UUIDv4(),
    });

    updatePartialState('images.data', (data) => ({
      loading: false,
      items: data.items
        .concat(result.data.data)
        .filter((item, index, array) => array.findIndex((i) => i.uuid === item.uuid) === index),
      hasMore: result.data.data.length && result.data.hasMore,
    }));
  } catch (error) {
    console.log(`[DEBUG] error`, error);
    updatePartialState('images.data', { loading: false, hasMore: false });
  }
};

export const loadMoreModelImages = async (type = 'case') => {
  try {
    const { modelUuid } = getPartialState('modelImages.modelData');
    const { versionUuid, page } = getPartialState(`modelImages.${type}.filters`);
    const { loading, hasMore } = getPartialState(`modelImages.${type}.data`);

    if (loading || !hasMore) return;

    updatePartialState(`modelImages.${type}.data`, { loading: true });
    updatePartialState(`modelImages.${type}.filters`, { page: page + 1 });

    const result = await request('/api/www/image/tool/modelCases', {
      modelUuid,
      versionUuid,
      modelType: 'model',
      imageType: type === 'case' ? 'sample' : 'return',
      sceneControl: 1,
      page: page + 1,
      pageSize: 30,
    });

    updatePartialState(`modelImages.${type}.data`, (data) => ({
      loading: false,
      items: data.items.concat(result.data.data),
      hasMore: result.data.data.length && result.data.hasMore,
    }));
  } catch (error) {
    console.log(`[DEBUG] error`, error);
    updatePartialState(`modelImages.${type}.data`, { loading: false, hasMore: false });
  }
};
